// Confirm-card action endpoints (chat-proposed actions). The approve POST is
// the ONLY path from an agent proposal to a running Temporal workflow; it is
// idempotency-keyed (double-click safe) and the server re-validates against
// current data (409 {status:"stale"|"expired"|...} when the world moved).
import { api } from "utils/axios";

export type ChatActionStatus =
	| "proposed"
	| "approved"
	| "executing"
	| "succeeded"
	| "failed"
	| "declined"
	| "expired"
	| "stale";

export interface IChatActionState {
	id: string;
	status: ChatActionStatus;
	title: string;
	summaryAmount?: string;
	preview: { label: string; detail: string }[];
	expiresAt?: string;
	workflowRunId?: string;
	error?: string;
}

export async function approveChatAction(
	proposalId: string,
	companyId: number,
	idempotencyKey: string,
): Promise<{
	status: ChatActionStatus;
	workflowRunId?: string;
	// Outcome note from an action that ran synchronously (e.g. "3 of 5 added").
	error?: string;
}> {
	const { data } = await api.post(`user/chat/actions/${proposalId}/approve`, {
		company_id: companyId,
		idempotency_key: idempotencyKey,
	});
	return data;
}

/**
 * Why the human rejected a proposal. Must match `AgentActionDeclineReason` in
 * equility-api — the endpoint validates against that exact set and 400s on
 * anything else.
 *
 * Only `incorrect` says the assistant was WRONG. The other three mean the
 * proposal was fine but unwanted here, and the backend records that distinction
 * so an accuracy metric never counts them as errors.
 */
export type ChatActionDeclineReason =
	| "incorrect"
	| "not_now"
	| "will_do_manually"
	| "other";

export async function declineChatAction(
	proposalId: string,
	companyId: number,
	reason?: ChatActionDeclineReason,
): Promise<{ status: ChatActionStatus }> {
	const { data } = await api.post(`user/chat/actions/${proposalId}/decline`, {
		company_id: companyId,
		// Omitted entirely when absent rather than sent as null: the column
		// distinguishes "not asked" from a deliberate "other", and the API DTO
		// treats the field as optional.
		...(reason ? { decline_reason: reason } : {}),
	});
	return data;
}

export async function getChatAction(
	proposalId: string,
	companyId: number,
): Promise<IChatActionState> {
	const { data } = await api.get(
		`user/chat/actions/${proposalId}?company_id=${companyId}`,
	);
	return data;
}

// A single bridge line for the amend workflow. Amounts here are in MAJOR units
// (dollars) — the api's toMinorUnits ingests them — unlike the *TotalCents
// fields on IProposeAmendArgs, which are integer cents. This split mirrors the
// existing amend-to-match payload (see utils/amendToMatch.ts).
export interface IAmendProposalLine {
	accountId: string;
	amount: number;
	classId?: string;
	description?: string;
}

// Args for the amend-to-match proposal. Identical crossing to
// buildAmendToMatchPayload: glTotalCents is the GL-list sum, sourceTotalCents is
// the bank/source sum. Build via buildAmendToMatchPayload so the FE↔DTO total
// crossing footgun stays out of call sites.
export interface IProposeAmendArgs {
	reconciliationId: string;
	sourceTransactionId: string;
	originalGlTransactionIds: string[];
	reconciledAccountType: string;
	reconciledAccountQboId: string;
	glTotalCents: number;
	sourceTotalCents: number;
	lines: IAmendProposalLine[];
	txnDate: string;
	description: string;
	/** QBO Customer/Vendor id for the bridge's "Name"; sent only with entityType. */
	entityId?: string;
	entityType?: "Customer" | "Vendor";
	/** Display name for the approval card only; QBO is stamped from entityId. */
	entityName?: string;
}

// The server-derived proposal returned by POST propose. It carries only the
// ledger-backed ids + the human-readable preview; the args stay server-side on
// the AgentActionRequest row. Shaped so it can be dropped straight into an
// `action` ChatBlock (via safeParseBlock).
export interface IChatActionProposal {
	proposalId: string;
	idempotencyKey: string;
	expiresAt?: string;
	asOf?: string;
	title: string;
	summaryAmount?: string;
	preview: { label: string; detail: string }[];
	// The api sends "amend-to-match"; optional as a defensive contract — the block
	// builder falls back to the same constant so the card always has a workflow.
	workflow?: string;
}

/**
 * Propose an amend-to-match action for human approval WITHOUT executing it. The
 * server persists an AgentActionRequest ledger row and returns the confirm-card
 * proposal; approval (via approveChatAction) is the only path to a run. This is
 * the results-page path: the exact selection payload is passed as `args` so the
 * agent never has to guess transaction ids.
 */
export async function proposeAmendAction(
	companyId: number,
	args: IProposeAmendArgs,
	sessionId?: string,
): Promise<IChatActionProposal> {
	const { data } = await api.post("user/chat/actions/propose", {
		company_id: companyId,
		workflow_key: "amend-to-match",
		args,
		...(sessionId ? { session_id: sessionId } : {}),
	});
	return data;
}

/**
 * Map a 409 response body onto a card status. The server answers conflicts
 * with {status: "..."} so the card can render the precise state (stale vs
 * expired vs already-executing) instead of a generic failure.
 */
export function conflictStatus(error: unknown): ChatActionStatus | null {
	const status = (error as { response?: { data?: { status?: string } } })
		?.response?.data?.status;
	if (
		status === "stale" ||
		status === "expired" ||
		status === "declined" ||
		status === "executing" ||
		status === "succeeded" ||
		status === "failed" ||
		status === "approved" ||
		status === "proposed"
	) {
		return status;
	}
	return null;
}
